/**
 * BoVio API - Offer check response
 */
export interface OfferCheck {
  /**
   * List of cancellation penalties
   */
  cancellationPenalties: CancellationPenalty[]
  /**
   * The cashback information.
   */
  cashback?: Cashback
  /**
   * The first charge attempt due date.
   */
  chargeDueAt?: null | string
  /**
   * The check-in from the offer.
   */
  checkIn: string
  /**
   * The check-in instructions to the customer
   */
  checkInInstructions?: string
  /**
   * The check-out from the offer.
   */
  checkOut: string
  /**
   * Currency conversion rate information.
   */
  currencyConversionRate?: null | CurrencyConversionRate
  /**
   * The deposit amount for Charge Later.
   */
  depositAmount?: null | string
  /**
   * Date and time (UTC) when the deal freeze expires.
   */
  expiresAt?: null | string
  /**
   * Extra parameters required to perform some action in the consumer side
   */
  extraParams?: null | ExtraParams
  /**
   * Links generated for the offer
   */
  links: Link[]
  /**
   * Room meals
   */
  meals?: string[]
  /**
   * Name of the entity that processes the customer payment
   */
  merchantOfRecord: MerchantOfRecord
  /**
   * Pay in Card Currency (PICC) eligibility information for the requested card currency.
   */
  payInCardCurrency?: PayInCardCurrency
  /**
   * What the offer is eligible for. `pay_at_property` means the payment will be made at the
   * property, `charge_later` means the offer is eligible for a charge later payment,
   * `pay_now` means the offer is only eligible for a pay now payment. The booking response
   * returns the same field with the mode that was consolidated.
   */
  paymentMode: PaymentMode
  /**
   * Prices
   */
  prices: Price[]
  /**
   * Provider code
   */
  providerCode?: null | string
  /**
   * Provider trader information
   */
  providerTraderInformation?: null | ProviderTraderInformation
  /**
   * Booking flow metadata id (re-check anchor) used to reproduce the original price on a
   * subsequent offer-check re-check. Set only for merchant-of-record regular offers; null for
   * assisted and bundle offers, which are not price-anchored.
   */
  recheckMetadataId?: null | string
  rooms?: Room[]
  /**
   * Offer status
   */
  status: Status
  /**
   * Offer metadata token used during the offer check
   */
  token: string
}

/**
 * Cancellation penalty
 */
export interface CancellationPenalty {
  /**
   * Fixed amount of the penalty
   */
  amount: string
  /**
   * Currency of the fixed amount
   */
  currency: string
  /**
   * End date and time of the penalty
   */
  end: string
  /**
   * Start date and time of the penalty
   */
  start: string
}

/**
 * The cashback information.
 */
export interface Cashback {
  /**
   * The type of cashback.
   */
  type?: CashbackType
  /**
   * The value of cashback.
   */
  value?: number
}

/**
 * The type of cashback.
 */
export enum CashbackType {
  Points = 'points'
}

export interface CurrencyConversionRate {
  /**
   * The source currency of the conversion
   */
  fromCurrency: string
  /**
   * The inverse exchange rate (1 / rate) from toCurrency to fromCurrency. Up to 10 decimal
   * digits, trailing zeros stripped.
   */
  inverseRate: string
  /**
   * The exchange rate from fromCurrency to toCurrency. Up to 10 decimal digits, trailing
   * zeros stripped.
   */
  rate: string
  /**
   * The target currency of the conversion
   */
  toCurrency: string
}

export interface ExtraParams {
  /**
   * Final provider code.
   */
  finalProviderCode?: string
  /**
   * Cardinal Commerce Credit Card validation code. Returned only in EPS offers
   */
  jwt?: string
}

/**
 * Link
 */
export interface Link {
  /**
   * URL
   */
  href: string
  /**
   * HTTP method
   */
  method: Method
  /**
   * Type
   */
  type: LinkType
}

/**
 * HTTP method
 */
export enum Method {
  Get = 'GET',
  Post = 'POST'
}

/**
 * Type
 */
export enum LinkType {
  BookingCreation = 'booking_creation',
  BookingCreationAsync = 'booking_creation_async',
  BundleBookingCreation = 'bundle_booking_creation',
  ChargeLaterBookingCreation = 'charge_later_booking_creation',
  PaymentMethodsSettings = 'payment_methods_settings'
}

/**
 * Name of the entity that processes the customer payment
 */
export enum MerchantOfRecord {
  Expedia = 'expedia',
  Findhotel = 'findhotel',
  Getaroom = 'getaroom',
  Hotel = 'hotel',
  Priceline = 'priceline'
}

/**
 * Pay in Card Currency (PICC) eligibility information for the requested card currency.
 */
export interface PayInCardCurrency {
  /**
   * Whether the offer is eligible for payment in the requested card currency
   */
  eligible?: boolean
  /**
   * The original chargeable currency
   */
  fromCurrency?: string
  /**
   * The inverse exchange rate (1 / rate) from toCurrency to fromCurrency. Only returned when
   * eligible=true
   */
  inverseRate?: null | string
  /**
   * The exchange rate from fromCurrency to toCurrency. Only returned when eligible=true
   */
  rate?: null | string
  /**
   * The card currency supplied in the offer-check request
   */
  toCurrency?: null | string
}

/**
 * What the offer is eligible for. `pay_at_property` means the payment will be made at the
 * property, `charge_later` means the offer is eligible for a charge later payment,
 * `pay_now` means the offer is only eligible for a pay now payment. The booking response
 * returns the same field with the mode that was consolidated.
 */
export enum PaymentMode {
  ChargeLater = 'charge_later',
  PayAtProperty = 'pay_at_property',
  PayNow = 'pay_now'
}

/**
 * Price
 */
export interface Price {
  /**
   * Chargeable information
   */
  chargeable: Chargeable
  /**
   * @deprecated Use chargeable.currency and hotelFees.currency instead.
   */
  currencyCode: string
  /**
   * Hotel fees information
   */
  hotelFees: HotelFees
  /**
   * Amount to be paid at the hotel. Hotel fees for pay now offers. Hotel fees and booking
   * total price for pay later offers. This object is not returned when the total value is not
   * available in the hotel currency.
   */
  payAtHotel?: null | PayAtHotel
  /**
   * Taxes information
   */
  taxes?: null | Taxes
  /**
   * The type of the price
   */
  type: PriceType
}

/**
 * Chargeable information
 */
export interface Chargeable {
  /**
   * Total rate without taxes
   */
  base: string
  /**
   * Currency of the chargeable price
   */
  currency: string
  /**
   * Total of all taxes
   */
  taxes: string
  /**
   * Total of base rate and taxes
   */
  total: string
}

/**
 * Hotel fees information
 */
export interface HotelFees {
  /**
   * The breakdown of the hotel fees per type
   */
  breakdown: HotelFeesBreakdown[]
  /**
   * Currency of the hotel fees
   */
  currency: string
  /**
   * Total of all the hotel fees
   */
  total: string
}

export interface HotelFeesBreakdown {
  /**
   * Raw charge inputs (mode + value) for the breakdown row. Emitted today only on local_vat
   * rows and omitted entirely otherwise.
   */
  charge?: HotelFeesBreakdownCharge
  /**
   * The elements of the hotel fees that composes the "other" type for more than one remaining
   * fee type.
   */
  elements?: TypeElement[]
  /**
   * The amount charged for the type
   */
  total?: string
  /**
   * The type of the hotel fees
   */
  type?: TypeElement
}

/**
 * Raw charge inputs (mode + value) for the breakdown row. Emitted today only on local_vat
 * rows and omitted entirely otherwise.
 */
export interface HotelFeesBreakdownCharge {
  /**
   * RAA charge mode that drove the calculation.
   */
  mode: Mode
  /**
   * Raw input value. For mode=percentage, expressed in percentage points ("18.0" = 18%).
   */
  value: string
}

/**
 * RAA charge mode that drove the calculation.
 */
export enum Mode {
  PerGuest = 'per_guest',
  PerGuestNight = 'per_guest_night',
  PerNightRoom = 'per_night_room',
  PerRoom = 'per_room',
  Percentage = 'percentage'
}

/**
 * The type of the hotel fees
 */
export enum TypeElement {
  CityTax = 'city_tax',
  CleaningFee = 'cleaning_fee',
  DestinationFee = 'destination_fee',
  EnvironmentFee = 'environment_fee',
  GoodsAndServicesTax = 'goods_and_services_tax',
  GovernmentTax = 'government_tax',
  LocalVat = 'local_vat',
  MunicipalityFee = 'municipality_fee',
  Other = 'other',
  ResortFee = 'resort_fee',
  ServiceCharge = 'service_charge',
  Tax = 'tax',
  TourismFee = 'tourism_fee'
}

export interface PayAtHotel {
  /**
   * Hotel currency
   */
  currency: string
  /**
   * Total to be paid at the hotel
   */
  total: string
}

export interface Taxes {
  /**
   * The breakdown of the taxes per type
   */
  breakdown: TaxesBreakdown[]
  /**
   * Currency of the taxes
   */
  currency: string
  /**
   * Total of all the taxes
   */
  total: string
}

export interface TaxesBreakdown {
  /**
   * The amount charged for the type
   */
  total?: string
  /**
   * The type of the taxes
   */
  type?: TaxesType
}

/**
 * The type of the taxes
 */
export enum TaxesType {
  Tax = 'tax',
  Vat = 'vat'
}

/**
 * The type of the price
 */
export enum PriceType {
  ChargeablePrice = 'chargeable_price',
  DisplayPrice = 'display_price',
  HotelPrice = 'hotel_price'
}

export interface ProviderTraderInformation {
  /**
   * Country code.
   */
  countryCode?: null | string
  /**
   * Trader name.
   */
  name?: null | string
  /**
   * Phone number
   */
  phoneNumber?: null | string
  /**
   * The URL of the terms and conditions. It can point to a PDF or a static HTML page.
   */
  termsAndConditionsUrl?: null | string
}

/**
 * Room information
 */
export interface Room {
  /**
   * Room amenities
   */
  amenities: string[]
  /**
   * Room amenities with structured metadata
   */
  amenitiesList?: Amenity[]
  /**
   * Room area
   */
  area: null | Area
  /**
   * Room bedrooms
   */
  bedrooms?: Bedroom[]
  /**
   * The check-in for each room.
   */
  checkIn: string
  /**
   * The check-out for each room.
   */
  checkOut: string
  /**
   * Room description
   */
  description: string
  /**
   * Translated room name to be displayed to the user
   */
  displayName?: string
  /**
   * Room ID
   */
  id: null | string
  /**
   * Room images
   */
  images: Image[]
  /**
   * Room master ID
   */
  masterId: null | string
  /**
   * Room meal plan offered for the rate, with optional occupancy rules
   */
  mealPlan?: MealPlan[]
  /**
   * Room meals
   */
  meals?: string[]
  /**
   * Room occupancy
   */
  occupancy?: null | Occupancy
  /**
   * Provider code
   */
  providerCode?: null | string
}

/**
 * Amenity with category and tag information
 */
export interface Amenity {
  /**
   * Translated amenity category name
   */
  category: string
  /**
   * Amenity category identifier
   */
  categoryId: string
  /**
   * Amenity identifier
   */
  id: string
  /**
   * Amenity tag (e.g. Free, Paid)
   */
  tag?: Tag
  /**
   * Translated amenity name
   */
  title: string
}

/**
 * Amenity tag (e.g. Free, Paid)
 */
export interface Tag {
  /**
   * Tag identifier
   */
  id: string
  /**
   * Tag name
   */
  name: string
}

export interface Area {
  /**
   * Room area in square feet
   */
  squareFeet?: number | null
  /**
   * Room area in square meters
   */
  squareMeters?: number | null
}

/**
 * Bedroom information
 */
export interface Bedroom {
  /**
   * Bed configurations
   */
  bedConfigurations: BedConfiguration[]
  /**
   * Bedroom description
   */
  description?: null | string
  /**
   * Bedroom ID
   */
  id?: null | string
  /**
   * Bedroom name
   */
  name?: null | string
}

/**
 * Bed configuration
 */
export interface BedConfiguration {
  /**
   * Bed configuration ID
   */
  cfgID?: number
  /**
   * Number of beds
   */
  count: number
  /**
   * Bed size
   */
  size?: null | string
  /**
   * Bed type
   */
  type: string
  /**
   * Bed type identifier
   */
  typeID?: string
}

/**
 * Room image
 */
export interface Image {
  /**
   * Image caption
   */
  caption?: string
  /**
   * Image url
   */
  url: string
}

/**
 * Meal plan entry
 */
export interface MealPlan {
  /**
   * Meal type included in the plan
   */
  meal: Meal
  /**
   * Occupancy the meal applies to
   */
  rule?: MealPlanRule
}

/**
 * Meal type included in the plan
 */
export enum Meal {
  AllInclusive = 'allInclusive',
  Breakfast = 'breakfast',
  Dinner = 'dinner',
  FullBoard = 'fullBoard',
  HalfBoard = 'halfBoard',
  Lunch = 'lunch'
}

/**
 * Occupancy the meal applies to
 */
export interface MealPlanRule {
  /**
   * Number of adults the meal applies to
   */
  adults?: number
  /**
   * Number of guests the meal applies to
   */
  guests?: number
  /**
   * Number of kids the meal applies to
   */
  kids?: number
}

export interface Occupancy {
  /**
   * Whether cribs are allowed
   */
  allowCribs?: boolean | null
  /**
   * Whether extra beds are allowed
   */
  allowExtraBeds?: boolean | null
  /**
   * Maximum number of extra beds allowed
   */
  extraBeds?: number | null
  /**
   * Maximum number of adults allowed
   */
  maxAdults?: number | null
  /**
   * Maximum number of children allowed
   */
  maxChildren?: number | null
  /**
   * Maximum total number of guests allowed
   */
  maxOccupancy?: number | null
}

/**
 * Offer status
 */
export enum Status {
  Available = 'available'
}
